// Angular imports
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { DatePipe } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { NgModule } from '@angular/core';

// Layouts
import { DefaultLayoutComponent } from './layouts/default/default.component';
import { ExtraLayoutComponent } from './layouts/extra/extra.component';

// Red Thread Components
import { BadgeComponent } from './rt-components/badge/badge.component';
import { BreadcrumbComponent } from './rt-components/breadcrumb/breadcrumb.component';
import { DialogHelpComponent } from './rt-components/navbar/dialog-help/dialog-help.component';
import { FileComponent } from './rt-components/file/file.component';
import { FooterComponent } from './rt-components/footer/footer.component';
import { LogoComponent } from './rt-components/logo/logo.component';
import { MainMenuComponent } from './rt-components/main-menu/main-menu.component';
import { NavbarComponent } from './rt-components/navbar/navbar.component';
import { RTCardComponent } from './rt-components/card/card.component';
import { SidebarComponent } from './rt-components/sidebar/sidebar.component';

// Red Thread Pages
import { AnalysisDetailsComponent } from './pages/analysis-details/analysis-details.component';
import { AuditTrailComponent } from './pages/audit/analysis-trail/analysis-trail.component';
import { AuthLogComponent } from './pages/audit/auth-log/auth-log.component';
import { BlogComponent } from './pages/blog/blog.component';
import { BlogDetailComponent } from './pages/blog/blog-detail/blog-detail.component';
import { ChromatogramsComponent } from './pages/chromatograms/chromatograms.component';
import { DialogComponent } from './pages/dashboard/dialog/dialog.component'
import { DocUploadComponent, FileUploadComponent } from './pages/doc-upload';
import { DownloadComponent } from './pages/download';
import { DynamicFormBuilderModule } from './pages/template/dynamic-form-builder/dynamic-form-builder.module';
import { GraphComponent } from './pages/small-molecule/graph/graph.component';
import { GraphDialogComponent } from './pages/small-molecule/graph-dialog/graph-dialog.component';
import { HeaderComponent } from './pages/home/header/component';
import { HelpDialogComponent } from './pages/doc-upload/help-dialog/help-dialog.component';
import { HomeComponent } from './pages/home/home.component';
import { LoadingComponent } from './pages/loading/component';
import { NewAnalysisComponent } from './pages/new-analysis/new-analysis.component';
import { PageNotFoundComponent } from './pages/not-found/not-found.component';
import { PreviousAnalysisComponent } from './pages/previous-analysis/previous-analysis.component';
import { ShowTableComponent } from './pages/previous-analysis/show-table/show-table.component';
import { SmallMoleculeComponent } from './pages/small-molecule/small-molecule.component';
import { TableComponent } from './pages/audit/analysis-trail/table/table.component';
import { TemplateComponent } from './pages/template/template.component';
import { UserForgotPasswordComponent } from './pages/amplify-auth/user-forgot-password/user-forgot-password.component';
import { UserNewPasswordComponent } from './pages/amplify-auth/user-new-password/user-new-password.component';
import { UserPageComponent } from './user-page/user-page.component';
import { UserSignInComponent } from './pages/amplify-auth/user-sign-in/user-sign-in.component';
import { UserSignOutComponent } from './pages/amplify-auth/user-sign-out/user-sign-out.component';

// Extra pages
import { MaterialModule } from './pages/material-components/material.module';
import { Page403Component } from './pages/extra-pages/page403/page403.component';
import { Page404Component } from './pages/extra-pages/page-404/page-404.component';
import { Page500Component } from './pages/extra-pages/page-500/page-500.component';
import { PageSignIn1Component } from './pages/extra-pages/sign-in-1/sign-in-1.component';

// Service components
import { AppLoaderComponent } from './service/app-loader/app-loader.component';
import { AppLoaderService } from './service/app-loader/app-loader.service';
import { CognitoUtil } from '../app/service/cognito.service'
import { TemplateService } from './service/template.service';

// Other
import 'hammerjs';
import Amplify from 'aws-amplify';
import awsconfig from '../environments/environment';
import { AuthInterceptor } from './shared/auth.interceptor';
import { ChartsModule } from 'ng2-charts';
import { FileSizePipe } from '../utils';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { ModalModule, BsModalService } from 'ngx-bootstrap/modal';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';


Amplify.configure(awsconfig);

@NgModule({
    declarations: [
        AnalysisDetailsComponent,
        AppComponent,
        AppLoaderComponent,
        AuditTrailComponent,
        AuthLogComponent,
        BadgeComponent,
        BlogComponent,
        BlogDetailComponent,
        BreadcrumbComponent,
        ChromatogramsComponent,
        DefaultLayoutComponent,
        DialogComponent,
        DialogHelpComponent,
        DocUploadComponent,
        DownloadComponent,
        ExtraLayoutComponent,
        FileComponent,
        FileSizePipe,
        FileUploadComponent,
        FooterComponent,
        GraphComponent,
        GraphDialogComponent,
        HeaderComponent,
        HelpDialogComponent,
        HomeComponent,
        LoadingComponent,
        LogoComponent,
        MainMenuComponent,
        NavbarComponent,
        NewAnalysisComponent,
        Page403Component,
        Page404Component,
        Page500Component,
        PageNotFoundComponent,
        PageSignIn1Component,
        PreviousAnalysisComponent,
        PreviousAnalysisComponent,
        RTCardComponent,
        ShowTableComponent,
        SidebarComponent,
        SmallMoleculeComponent,
        TableComponent,
        TemplateComponent,
        UserForgotPasswordComponent,
        UserNewPasswordComponent,
        UserPageComponent,
        UserSignInComponent,
        UserSignOutComponent,
    ],
    exports: [
        MaterialModule,
        MatSortModule,
    ],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        CommonModule,
        DynamicFormBuilderModule,
        FormsModule,
        MatBadgeModule,
        MatButtonModule,
        MatCardModule,
        MatRadioModule,
        MatSortModule,
        MaterialModule,
        ModalModule,
        NgIdleKeepaliveModule.forRoot(),
        NgbModule,
        ReactiveFormsModule,
        ToastrModule.forRoot(),
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        MatNativeDateModule, AppLoaderService, CognitoUtil, DatePipe,
        provideHttpClient(withInterceptorsFromDi())
    ]
})

export class AppModule { }
